import React, {
  Fragment,
  useEffect,
} from 'react';
import {
  useSelector,
  useDispatch,
} from 'react-redux';
import PropTypes from 'prop-types';
import {
  Button,
  Grid,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  List,
  ListItemText,
  ListItem,
  ListItemIcon,
  Icon,
  Divider,
  CircularProgress,
  makeStyles,
} from '@material-ui/core';

import { getTenantPreviewInvoice } from '@pingum/app-state-management/tenant/actions';

import { getNumberValue } from '../../../../../../../utils/pricingHelper';
import { getTenantFromUrl } from '../../../../../../../utils/urlHelper';

import styles from './styles';
import { formatDateOnly } from '../../../../../../../utils/dateHelper';

const useStyles = makeStyles(styles);

export const ConfirmSubscribeToPlanModalComponent = ({
  isOpen,
  handleClose,
  handleCloseConfirm,
  currentBillingInformation,
  newUserLimit,
  newPeriod,
}) => {
  const classes = useStyles();
  const dispatch = useDispatch();

  const {
    isLoadingStripePreviewInvoice,
    stripePreviewInvoice,
  } = useSelector((state) => state.tenant);

  useEffect(() => {
    if (isOpen) {
      dispatch(getTenantPreviewInvoice(getTenantFromUrl(), newPeriod.perUserPlan, newUserLimit));
    }
  }, [isOpen]);

  const invoiceLines = (stripePreviewInvoice && stripePreviewInvoice.lines
    && stripePreviewInvoice.lines.data && stripePreviewInvoice.lines.data.length > 0)
    ? stripePreviewInvoice.lines.data
    : null;
  const invoiceDiscounts = (stripePreviewInvoice && stripePreviewInvoice.total_discount_amounts
      && stripePreviewInvoice.total_discount_amounts.length > 0)
    ? stripePreviewInvoice.total_discount_amounts
    : null;

  return (
    <div>
      { isOpen
      && (
        <Dialog
          open={isOpen}
          onClose={handleClose}
          aria-labelledby="cofirm-subscription-dialog-title"
          aria-describedby="cofirm-subscription-dialog-description"
          fullWidth
          maxWidth="md"
        >
          <DialogTitle id="cofirm-subscription-dialog-title">Confirm Subscription &amp; Preview Invoice</DialogTitle>

          {
            isLoadingStripePreviewInvoice
              ? (
                <Grid container justify="center">
                  <Grid item xs={12} className={classes.loadingGridItem}><CircularProgress /></Grid>
                  <Grid item xs={12} className={classes.loadingGridItem}><Typography variant="caption" gutterBottom>Loading invoice preview...</Typography></Grid>
                </Grid>
              )
              : (
                <DialogContent>
                  <Typography variant="h4" gutterBottom>Please review and confirm the following changes to your account.</Typography>
                  <List>
                    {
                      (currentBillingInformation.isOnFreeTrial)
                      && (
                        <ListItem>
                          <ListItemIcon>
                            <Icon>info</Icon>
                          </ListItemIcon>
                          <ListItemText
                            primary="Free Trial Still Active"
                            secondaryTypographyProps={{ component: 'div' }}
                            secondary={(
                              <Fragment>
                                <Typography
                                  component="div"
                                  variant="caption"
                                >
                                  Your organization still has an active free trial.
                                  You will not be charged until your trial is over.
                                  (in {currentBillingInformation.trialDaysRemaining} days).
                                </Typography>
                              </Fragment>
                            )}
                          />
                        </ListItem>
                      )
                    }
                    { (currentBillingInformation.isOnFreeTrial) && (<Divider component="li" />) }
                    <ListItem>
                      <ListItemText
                        primaryTypographyProps={{
                          component: 'h4',
                          variant: 'h4',
                        }}
                        primary="Order Details"
                        secondaryTypographyProps={{ component: 'div' }}
                        secondary={(
                          <Fragment>
                            {
                              invoiceLines && invoiceLines.map((invoiceLine) => (
                                <div key={invoiceLine.id} className={classes.detailsContainer}>
                                  <span className={classes.quantity}>{invoiceLine.quantity}</span>
                                  <div className={classes.labelContainer}>
                                    <div className={classes.label}>{invoiceLine.type === 'subscription'
                                      ? 'New Subscription'
                                      : 'Prorated Item'}
                                    </div>
                                    <div className={classes.description}>{invoiceLine.description}</div>
                                  </div>
                                  <span className={[classes.unitRate]}>
                                    ${getNumberValue(invoiceLine.amount / 100)}
                                  </span>
                                </div>
                              ))
                            }
                            <div className={classes.subtotalContainer}>
                              <span className={classes.quantity} />
                              <div className={classes.labelContainer}>
                                <div className={classes.label}>Subtotal</div>
                              </div>
                              <span className={[classes.unitRate]}> ${getNumberValue(stripePreviewInvoice.subtotal / 100)} </span>
                            </div>
                            {
                              invoiceDiscounts && (
                                <div>
                                  <Divider component="div" className={classes.orderDetailsDivider} />
                                  { invoiceDiscounts.map((invoiceDiscount) => (
                                    <div key={invoiceDiscount.discount} className={classes.detailsContainer}>
                                      <span className={classes.quantity} />
                                      <div className={classes.labelContainer}>
                                        <div className={classes.label}>Discount</div>
                                        <div className={classes.description}>
                                          {stripePreviewInvoice.discount.coupon.name} - {stripePreviewInvoice.discount.coupon.percent_off}% Off
                                        </div>
                                      </div>
                                      <span className={[classes.unitRate]}>
                                        $-{getNumberValue(invoiceDiscount.amount / 100)}
                                      </span>
                                    </div>
                                  ))}
                                </div>
                              )
                            }
                            <Divider component="div" className={classes.orderDetailsDivider} />
                            <div className={classes.detailsContainer}>
                              <span className={classes.quantity} />
                              <div className={classes.labelContainer}>
                                <div className={classes.label}>Total</div>
                              </div>
                              <span className={[classes.unitRate]}>${getNumberValue(stripePreviewInvoice.total / 100)}</span>
                            </div>
                            {
                              (stripePreviewInvoice.starting_balance < 0
                                && (stripePreviewInvoice.starting_balance - stripePreviewInvoice.ending_balance) < 0) && (
                                  <div>
                                    <Divider component="li" className={classes.orderDetailsDivider} />
                                    <div className={classes.detailsContainer}>
                                      <span className={classes.quantity} />
                                      <div className={classes.labelContainer}>
                                        <div className={classes.label}>Applied Balance</div>
                                        <div className={classes.description}>Credits available on account</div>
                                      </div>
                                      <span className={[classes.unitRate]}>
                                        ${getNumberValue(Math.abs((stripePreviewInvoice.starting_balance
                                          - stripePreviewInvoice.ending_balance)) / 100)}
                                      </span>
                                    </div>
                                    { stripePreviewInvoice.ending_balance < 0 && (
                                      <div className={classes.detailsContainer}>
                                        <span className={classes.quantity} />
                                        <div className={classes.labelContainer}>
                                          <div className={classes.label}>Remaining Balance</div>
                                          <div className={classes.description}>Balance remaining on account</div>
                                        </div>
                                        <span className={[classes.unitRate]}>
                                          ${getNumberValue(Math.abs(stripePreviewInvoice.ending_balance) / 100)}
                                        </span>
                                      </div>
                                    )}
                                  </div>
                              )
                            }
                            <Divider component="div" className={classes.orderDetailsDivider} />
                            <div className={classes.detailsContainer}>
                              <span className={classes.quantity} />
                              <div className={classes.labelContainer}>
                                <div className={classes.totalLabel}>Amount Due Today</div>
                              </div>
                              <span className={[classes.totalUnitRate]}>
                                ${currentBillingInformation.isOnFreeTrial
                                ? 0
                                : getNumberValue(stripePreviewInvoice.amount_due / 100)}
                              </span>
                            </div>
                            {
                              currentBillingInformation.isOnFreeTrial && (
                                <div className={classes.detailsContainer}>
                                  <span className={classes.quantity} />
                                  <div className={classes.labelContainer}>
                                    <div className={classes.totalLabel}>Amount Due {formatDateOnly(currentBillingInformation.freeTrialEndDate)}</div>
                                  </div>
                                  <span className={[classes.totalUnitRate]}>
                                    ${getNumberValue(stripePreviewInvoice.amount_due / 100)}
                                  </span>
                                </div>
                              )
                            }
                          </Fragment>
                        )}
                      />
                    </ListItem>
                  </List>
                  {
                    (invoiceLines && invoiceLines.filter((invoiceLine) => invoiceLine.proration).length > 0) && (
                      <Typography variant="caption">&#42;Any prorated values are estimates. Exact values are calculated at the time of subscription confirmation.</Typography>
                    )
                  }
                </DialogContent>
              )
          }

          <DialogActions>
            <Button onClick={handleClose} disabled={isLoadingStripePreviewInvoice}>
              Cancel
            </Button>
            <Button onClick={handleCloseConfirm} color="primary" variant="contained" autoFocus disabled={isLoadingStripePreviewInvoice || !invoiceLines}>
              Confirm Subscription
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </div>
  );
};

ConfirmSubscribeToPlanModalComponent.propTypes = {
  isOpen: PropTypes.bool,
  handleClose: PropTypes.func,
  handleCloseConfirm: PropTypes.func,
  currentBillingInformation: PropTypes.object,
  newUserLimit: PropTypes.number,
  newPeriod: PropTypes.object,
};

ConfirmSubscribeToPlanModalComponent.defaultProps = {
  isOpen: false,
  handleClose: () => {},
  handleCloseConfirm: () => {},
  currentBillingInformation: {},
  newUserLimit: 0,
  newPeriod: 'Missing - please contact support',
};

export default ConfirmSubscribeToPlanModalComponent;
